import _ from 'underscore';
import Backbone from 'backbone';
import $ from 'jquery';
import { startsWith } from 'underscore.string';
import Promise from 'bluebird';
import {
  customColumnGridKeyFromId,
  parseIdFromGridKey,
  getCustomColumnPreferenceName,
  ODATA_PREFIX,
} from '@bingads-webui-universal/custom-column-utils';
import { Dialog } from '@bingads-webui/dialog';
import { ColumnChooser } from '@bingads-webui/column-chooser';
import { ColumnChooserEditablePicker } from '@bingads-webui/column-chooser-editable-picker';
import { CustomColumn } from '@bingads-webui/projection-grid-custom-column';
import { initializePop } from '@bingads-webui-campaign/common-grid-config';
import { isValidForFeature } from '@bingads-webui/custom-columns-shared-config';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { buildColumnConversions } from '@bingads-webui-campaign/column-conversions';
import { normalizeConfig } from './config';
import { TextView } from './prompt-text-view';
import {
  initialize as initializeUpdateFilterForCustomColumn,
  customColumnOperations,
  updateFilterSchemaForCustomColumns,
  cleanUpCustomColumnFilter,
} from './update-filter-for-custom-column';

const dropdownId = 'dropdown-column-chooser';
const saveListId = 'dropdown-radio-group-saved-columns';

class ColumnChooserAgent {
  constructor() {
    _.extend(this, Backbone.Events);
    this.recordRemoveEventName = 'saved-record-removed';
    this.recordSaveEventName = 'new-record-saved';
    this.recordChangeEventName = 'selected-record-changed';
    this.selectColumnsChangedEventName = 'selected-columns-changed';
  }

  triggerSelectedColumnsChangedEvent(names) {
    this.trigger(this.selectColumnsChangedEventName, names);
  }

  triggerSelectedRecordChangedEvent() {
    this.trigger(this.recordChangeEventName);
  }

  triggerRecordRemovedEvent() {
    this.trigger(this.recordRemoveEventName);
  }

  triggerRecordSavedEvent() {
    this.trigger(this.recordSaveEventName);
  }

  onSelectedRecordChanged(callback) {
    this.on(this.recordChangeEventName, callback);
  }

  onRecordRemoved(callback) {
    this.on(this.recordRemoveEventName, callback);
  }

  onRecordSaved(callback) {
    this.on(this.recordSaveEventName, callback);
  }

  onSelectedColumnsChanged(callback) {
    this.on(this.selectColumnsChangedEventName, callback);
  }
}

export const columnChooserPlugin = definePlugin => definePlugin('columnChooser', [
  'config',
  'gridView',
  'toolbar',
], (config, gridView, toolbar) => {
  const columnChooserAgent = new ColumnChooserAgent();
  let columnChooser = null;
  let customColumnPanel = null;
  let customColumnService = null;
  let filterService = null;
  let entitySchemaName = null;
  let columnChooserEditablePicker = null;

  const permissions = config.config && _.isFunction(config.config.get) ? config.config.get('Permissions') : {};
  const { dynamic = {} } = permissions || {};
  const { ColumnConversion: IsColumnConversionEnabled } = dynamic;

  const pluginConfig = normalizeConfig(config);
  const {
    categories, customColumnConfig, columnProcessorBeforeSetRecord, enableRSAAdCustomizer,
  } = pluginConfig;
  const isCustomColumnEnabled = customColumnConfig && customColumnConfig.enabled;
  const safeGetString = key => (pluginConfig.i18n.getString(key) || key);
  const toolbarEditor = pluginConfig.toolbar ||
    toolbar.gridToolbarGroup.toolbarWithItem(dropdownId);
  const query = {
    project: 'CampaignUI',
    grid: config.name,
  };
  let adCustomizerAttributeColumns = [];
  let applyColumnConversions;
  if (IsColumnConversionEnabled) {
    ({ applyColumnConversions } = buildColumnConversions(pluginConfig.columnConversions));
  }

  if (isCustomColumnEnabled) {
    ({ filterService } = customColumnConfig);
    entitySchemaName = filterService.entitySchema.name;
    initializePop({ i18n: pluginConfig.i18n });
    initializeUpdateFilterForCustomColumn({ filterService, safeGetString, permissions });
  }

  function getSelectedColumns() {
    return _.map(gridView.get('columns'), _.property('name'));
  }

  function updateGridConfig() {
    const queryParams = gridView.get('query');
    const { entitySet } = queryParams.options;
    const action = entitySet.navigation.source;
    const existingGridDataParameters = entitySet.navigation.parameters.gridDataParameters || {};
    const gridDataParameters = _.defaults({
      CustomColumns: undefined,
    }, existingGridDataParameters);
    const parameters = _.defaults({ gridDataParameters }, entitySet.navigation.parameters);

    return _.defaults({
      options: _.defaults({
        entitySet: action.$call(parameters),
      }, queryParams.options),
    }, queryParams);
  }

  function setSelectedColumns(names) {
    const {
      columns,
      locked,
      patchQuery,
      mandatoryProperties,
    } = pluginConfig;
    const patch = {};
    let customColumns;

    if (isCustomColumnEnabled && customColumnService) {
      customColumns = customColumnService.getAll();

      _.each(customColumns, (columnInfo) => {
        columns[customColumnGridKeyFromId(columnInfo.id)] = new CustomColumn(_.defaults({
          i18n: pluginConfig.i18n,
        }, columnInfo)).toJSON();
      });
    }

    if (enableRSAAdCustomizer && permissions.IsRSAAdCustomizerEnabled) {
      _.each(adCustomizerAttributeColumns, (columnInfo) => {
        if (columnInfo && columnInfo.Id) {
          columns[`AdCustomizerAttributes${columnInfo.Id}`] =
          new pluginConfig.RSAAdCustomizerAttributeColumn(_.defaults({
            gridView,
            resource: pluginConfig.resource,
            i18n: pluginConfig.i18n,
            i18nErrors: pluginConfig.i18nErrors,
            gridType: entitySchemaName,
            permissions,
          }, columnInfo)).toJSON();
        }
      });
    }

    const validNames = _.chain(locked)
      .concat(names)
      .filter(name => _.has(columns, name))
      .uniq()
      .value();

    patch.columns = _.map(validNames, name => _.defaults({ name }, columns[name]));

    if (patchQuery && !config.isDataFetchManagerSource) {
      let select = _.chain(validNames).reduce((memo, name) => {
        const column = columns[name];
        const property = column && (column.property || column.name);
        const key = (_.isString(property) && property) ||
          (_.isObject(property) && property.key);

        if (_.isArray(key)) {
          Array.prototype.push.apply(memo, key);
        } else if (key) {
          memo.push(key);
        }

        return memo;
      }, []).union(mandatoryProperties).value();

      let patchCustomColumns = {};

      if (isCustomColumnEnabled && !_.isEmpty(customColumns)) {
        const selectedCustomColumnNames = _.filter(validNames, name =>
          startsWith(name, ODATA_PREFIX));
        const selectedCustomColumns = _.filter(customColumns, column =>
          _.contains(selectedCustomColumnNames, customColumnGridKeyFromId(column.id)));

        patchCustomColumns = updateGridConfig(selectedCustomColumns);
      }

      select = select.concat(config.customerHierarchyExtras || []);

      patch.query = _.defaults(
        { select },
        patchCustomColumns,
        patch.query || {}
      );
    }

    if (isCustomColumnEnabled && !_.isEmpty(customColumns)) {
      gridView.set(patch);
    } else {
      gridView.patch(patch);
    }

    if (config.isDataFetchManagerSource) {
      gridView.refresh(true);
    }

    gridView.trigger('columnChanged', { columnNames: validNames });

    // This is a workaround for the column alignment issue on IE & Edge
    // After redrawing the table with new column set, IE & Edge doesn't relayout
    // the table correctly. We need to detach and attach the table element after render.
    // we can remove this after Chromium-base Edge become the default version
    gridView.once('didRedraw', () => {
      const elTable = gridView.$('table, .table').get(-1);
      const $table = $(elTable);
      const $parent = $(elTable.parentElement);

      if (elTable) {
        $table.detach();
        $table.appendTo($parent);
      }
    });

    columnChooserAgent.triggerSelectedColumnsChangedEvent(names);
  }

  const dynamicCustomColumnsSetup = resolveSetup =>
    Promise.all([
      import(/* webpackMode: 'eager' */'@bingads-webui-campaign/custom-column-backbone-wrapper'),
      import(/* webpackMode: 'eager' */'@bingads-webui/custom-column-service-wrapper'),
      import(/* webpackMode: 'eager' */'@bingads-webui/page-ready'),
    ]).spread(({ CustomColumnBackboneWrapper }, { CustomColumnServiceWrapper }, { PageReady }) => {
      customColumnService = new CustomColumnServiceWrapper({
        name: getCustomColumnPreferenceName(config.name),
        CustomColumns: pluginConfig.CustomColumns,
      });

      // update preference store
      PageReady.afterPageReady().then(() => {
        customColumnService.updateCustomColumnPreferenceStore().then(({
          removedColumns,
          editedColumns,
          addedColumns,
        }) => {
          if (permissions.IsCustomColumnFilterSupported) {
            _.each(editedColumns, (editedColumn) => {
              updateFilterSchemaForCustomColumns(editedColumn, customColumnOperations.edit);
            });

            _.each(addedColumns, (addedColumn) => {
              updateFilterSchemaForCustomColumns(addedColumn, customColumnOperations.add);
            });

            _.each(removedColumns, ({ id: customColumnId }) => {
              const id = customColumnId.replace('CustomColumn', '');
              cleanUpCustomColumnFilter(customColumnId);
              updateFilterSchemaForCustomColumns({ id }, customColumnOperations.remove);
            });
          }
        }).catch((err) => {
          throw new Error(`dynamicCustomColumnsSetup: ${JSON.stringify(err)}}`);
        });
      });

      const openCustomColumnPanel = options => new Promise((resolvePanel, rejectPanel) => {
        const isEdit = _.has(options, 'key');
        let id;
        let initialName;
        let initialDescription;
        let initialColumnFormat;
        let initialExpression;

        let p$columnDetail;

        if (isEdit) {
          id = parseIdFromGridKey(options.key);
          p$columnDetail = customColumnService.getCustomColumnDetailFromMT(id)
            .then((detail) => {
              if (detail && !_.isEmpty(detail) && detail.id === id) {
                ({
                  name: initialName,
                  description: initialDescription,
                  format: initialColumnFormat,
                  expression: initialExpression,
                } = detail);
              }
              return true;
            }).catch((data) => {
              const { errors } = data;
              const error = _.first(errors);

              if (error && error.Code === 'EntityDoesNotExist') {
                customColumnService.updateCustomColumnPreferenceStore(true);
                notificationApi.showWarning(pluginConfig.i18n.getString('CustomColumn_DoesNotExist'));
                columnChooserEditablePicker.removeCustomColumnByKey(options.key);
              }
              return false;
            });
        } else {
          p$columnDetail = Promise.resolve(true);
        }

        p$columnDetail.then((open) => {
          if (!open) {
            return;
          }

          customColumnPanel = new CustomColumnBackboneWrapper({
            props: {
              i18n: _.defaults({ getString: safeGetString }, pluginConfig.i18n),
              id,
              initialName,
              initialDescription,
              initialColumnFormat,
              initialExpression,
              metricCategories: customColumnConfig.metricCategories,
              segmentCategories: customColumnConfig.segmentCategories,
              resolvePanel,
              customColumnService,
              isEdit,
              entitySchemaName,
              permissions,
              afterSave: (formData) => {
                try {
                  if (isEdit && formData.id === undefined) {
                    customColumnService.updateCustomColumnPreferenceStore(true);
                    columnChooserEditablePicker.removeCustomColumnByKey(options.key);

                    return;
                  }

                  if (permissions.IsCustomColumnFilterSupported) {
                    updateFilterSchemaForCustomColumns(
                      formData,
                      isEdit ? customColumnOperations.edit : customColumnOperations.add
                    );
                  }

                  if (isEdit) {
                    const currentColumns = gridView.get('columns');
                    const isSelectedColumn = _.findIndex(
                      currentColumns,
                      column => `PerformanceMetrics/CustomColumn${formData.id}` === column.name
                    ) >= 0;

                    if (isSelectedColumn) {
                      gridView.refresh(true);
                    }
                  }
                } catch (e) {
                  rejectPanel(_.result(formData, 'id', 'MISSING_ID'));
                }
              },
            },
          });

          customColumnPanel.render();
        });
      });

      pluginConfig.categories.push({
        name: safeGetString('ColumnChooser_CustomColumnsCategory'),
        optionListViewFactory: ({ categoryName }) => {
          columnChooserEditablePicker = new ColumnChooserEditablePicker({
            i18n: pluginConfig.i18n,
            categoryName,
            isReadOnlyUser: permissions.IsReadOnlyUser,
            options: _.map(customColumnService.getAll(), column => ({
              key: customColumnGridKeyFromId(column.id),
              name: column.name,
              isValid: isValidForFeature({
                schemaName: entitySchemaName,
                expression: column.expression,
                permissions,
              }),
            })),
            p$removeColumn: (columnId) => {
              try {
                customColumnService.remove(columnId);

                if (permissions.IsCustomColumnFilterSupported) {
                  const customColumnId = columnId.split('/')[1];
                  const id = customColumnId.replace('CustomColumn', '');
                  cleanUpCustomColumnFilter(customColumnId);
                  updateFilterSchemaForCustomColumns({ id }, customColumnOperations.remove);
                  const currentColumns = gridView.get('columns');
                  const filtered = _.chain(currentColumns)
                    .filter(column => `PerformanceMetrics/CustomColumn${id}` !== column.name)
                    .map(column => column.name)
                    .value();

                  if (_.size(filtered) < _.size(currentColumns)) {
                    setSelectedColumns(filtered);
                  }
                }
              } catch (e) {
                return Promise.reject(columnId);
              }

              return Promise.resolve(columnId);
            },
            p$editColumn: openCustomColumnPanel,
            p$createColumn: openCustomColumnPanel,
          });

          return columnChooserEditablePicker;
        },
      });

      resolveSetup();
    });

  const setupCustomColumns = () => (isCustomColumnEnabled ?
    new Promise(dynamicCustomColumnsSetup) :
    Promise.resolve());

  const setupAdCustomizerAttributesColumns =
    () => (enableRSAAdCustomizer && permissions.IsRSAAdCustomizerEnabled ?
      new Promise((resolve) => {
        pluginConfig.AdCustomizerAttribute
          .findAll({
            query: {
              $top: 100,
              $orderby: 'Name asc',
            },
          }).then((returnResult) => {
            adCustomizerAttributeColumns = returnResult;
            pluginConfig.categories.push({
              name: safeGetString('AdCustomizerAttributes'),
              options: _.map(returnResult, (item) => {
                const {
                  Name,
                  Datatype,
                  Id,
                } = item || {};
                const dataTypeI18nKey = `RSAAdCustomizer_Attribute_${Datatype}`;
                return {
                  name: `${Name} (${pluginConfig.i18n.getString(dataTypeI18nKey)})`,
                  key: `AdCustomizerAttributes${Id}`,
                };
              }),
            });
            resolve();
          });
      }) :
      Promise.resolve());

  if (isCustomColumnEnabled) {
    gridView.on('didReload', () => {
      const { dataSource: { rawData } } = gridView;
      const customColumnResponseKey = '@ns.customcolumnerrorresponse';
      const customColumnErrors = rawData && rawData[customColumnResponseKey] ?
        rawData[customColumnResponseKey].CustomColumnErrors : [];

      if (_.isEmpty(customColumnErrors)) {
        return;
      }

      gridView.trigger('hasInvalidOrDeletedCustomColumns', customColumnErrors, true);
    });

    gridView.on('hasInvalidOrDeletedCustomColumns', (customColumnErrors, showFilterBar = false) => {
      customColumnService.updateCustomColumnPreferenceStore(true);

      const columnsToExclude = _.chain(customColumnErrors).map((item) => {
        if (item.ErrorCode === 'EntityDoesNotExist') {
          return { id: item.Id };
        }
        return null;
      }).compact().value();
      const columnsToInvalidate = _.chain(customColumnErrors).map((item) => {
        if (item.ErrorCode === 'EntityIsUnsupported' || item.ErrorCode === 'InternalError') {
          return { id: item.Id };
        }
        return null;
      }).compact().value();

      const currentColumns = gridView.get('columns');
      const filtered = _.chain(currentColumns)
        .filter(column => _.findIndex(customColumnErrors, excluded => `PerformanceMetrics/CustomColumn${excluded.Id}` === column.name) < 0)
        .map(column => column.name)
        .value();

      if (_.size(filtered) < _.size(currentColumns)) {
        setSelectedColumns(filtered);
      }

      filterService.trigger(
        'have-invalid-or-removed-properties',
        {
          deletedProperties: _.map(columnsToExclude, ({ id }) => `CustomColumn${id}`),
          invalidProperties: _.map(columnsToInvalidate, ({ id }) => `CustomColumn${id}`),
          showFilterBar,
        }
      );
    });
  }

  function getSelection() {
    return _.first(pluginConfig.SelectedColumns.filter({ where: query }));
  }

  function getSelectedRecord() {
    return _.result(getSelection(), 'name', 'Custom');
  }

  function setSelectedRecord(name) {
    const selection = getSelection();

    if (selection) {
      return pluginConfig.SelectedColumns.update(selection.id, { name });
    }

    return pluginConfig.SelectedColumns.create(_.defaults({ name }, query));
  }

  function refreshMenu() {
    toolbarEditor.update({
      id: saveListId,
      items: _.reduce(pluginConfig.Columns.filter({ where: query }), (memo, { name }) => {
        if (name !== 'Custom') {
          memo.push({
            text: name,
            value: name,
          });
        }
        return memo;
      }, [{
        text: pluginConfig.i18n.getString('ColumnsMenu_Custom'),
        value: 'Custom',
        locked: true,
      }]),
      value: getSelectedRecord(),
    });
  }

  function getRecord(name) {
    return _.first(pluginConfig.Columns.filter({
      where: _.defaults({ name }, query),
    }));
  }

  function setRecord(name, _columns) {
    const record = getRecord(name);
    let columns = _columns;
    if (_.isFunction(columnProcessorBeforeSetRecord)) {
      columns = columnProcessorBeforeSetRecord(_columns, record && record.columns);
    }

    if (permissions.dynamic && permissions.dynamic.AppCampaignNewCampaignGridColumns && record) {
      // do not remove column names that are not in the grids available columns. This is to be able to have
      // different columns set for different global filter using same preference key
      const unavailableSavedColumns = _.difference(record.columns, Object.keys(pluginConfig.columns));
      columns = columns.concat(unavailableSavedColumns);
    }

    columns = _.unique(columns);

    let p$setRecord;
    if (record) {
      p$setRecord = pluginConfig.Columns.update(record.id, { columns });
    } else {
      p$setRecord = pluginConfig.Columns.create(_.defaults({ columns, name }, query));
    }
    p$setRecord.then(() => {
      columnChooserAgent.triggerRecordSavedEvent();
    });
    return p$setRecord;
  }

  function applyRecord(name) {
    const promises = [];
    const record = getRecord(name);

    if (record) {
      if (permissions.IsAvgPositionDeprecationPhase2Enabled &&
        (!pluginConfig.isHotelCenterGrid || permissions.IsAvgPositionDeprecationPhase3Enabled)) {
        let selectedColumns = record.columns;
        const hasAveragePosition = selectedColumns.includes('AveragePosition');
        if (hasAveragePosition) {
          const hasTopImpressionRate = selectedColumns.includes('TopImpressionRate');
          const hasAbsoluteTopImpressionRate = selectedColumns.includes('AbsoluteTopImpressionRate');
          selectedColumns = selectedColumns.filter(column => column !== 'AveragePosition');
          if (!hasTopImpressionRate) {
            selectedColumns.push('TopImpressionRate');
          }
          if (!hasAbsoluteTopImpressionRate) {
            selectedColumns.push('AbsoluteTopImpressionRate');
          }
          promises.push(setRecord(name, selectedColumns));
        }
        if (IsColumnConversionEnabled) {
          selectedColumns = applyColumnConversions(selectedColumns);
        }
        setSelectedColumns(selectedColumns);
      } else if (IsColumnConversionEnabled) {
        const selectedColumns = applyColumnConversions(record.columns);
        setSelectedColumns(selectedColumns);
      } else {
        setSelectedColumns(record.columns);
      }
    } else {
      setSelectedColumns(pluginConfig.selected);
      promises.push(setRecord(name, pluginConfig.selected));
    }

    if (name !== getSelectedRecord()) {
      promises.push(setSelectedRecord(name));
    }

    return Promise.all(promises)
      .tap(() => columnChooserAgent.triggerSelectedRecordChangedEvent())
      .thenReturn();
  }

  const legacyQuery = {};

  function legacyDataProcesser() {
    const promises = [];
    const legacySelected = pluginConfig.LegacySelectedColumns.filter({ where: legacyQuery });
    const legacyRecords = pluginConfig.LegacyColumns.filter({ where: legacyQuery });

    // deal with selected columnchooser preference in legacy grid
    if (legacySelected.length > 0) {
      promises.push(setSelectedRecord(legacySelected[0].name)
        .then(getSelectedRecord)
        .then(applyRecord)
        .then(refreshMenu));
    }

    // deal with columnchooser preference in legacy grid
    _.each(legacyRecords, (legacyRecord) => {
      // this id means the record is from gridName_selected, this is not what we want here.
      // this is a hack to fix the issue that legacy grid save custom record as gridName_custom
      // which break the naming convention
      if (legacyRecord.id === -1) {
        return;
      }

      const record = getRecord(legacyRecord.name);
      const columns = _.map(
        legacyRecord.columns,
        col => (pluginConfig.nameFromLegacy && pluginConfig.nameFromLegacy[col]) || col
      );

      if (record) {
        const update = pluginConfig.Columns
          .update(record.id, { columns });

        promises.push(update);
      } else {
        const creation = pluginConfig.Columns
          .create(_.defaults({
            columns,
            name: legacyRecord.name,
          }, query));

        promises.push(creation);
      }
    });

    return Promise.all(promises);
  }

  function legacyHandler() {
    if (config.legacyGridName && pluginConfig.LegacyGridFlag) {
      const legacyGridFlagQuery = {
        project: 'CampaignUI',
        grid: config.legacyGridName,
      };

      _.extend(legacyQuery, {
        project: 'CampaignUI',
        grid: config.legacyGridName,
      });

      pluginConfig.LegacyGridFlag.findAll({ where: legacyGridFlagQuery })
        .then((flags) => {
          const flag = _.first(flags);

          if (flag && flag.flagInfo && flag.flagInfo.isColumnChooserMigrated) {
            return Promise.resolve();
          }

          if (flag) {
            pluginConfig.LegacyGridFlag.update(
              flag.id,
              { flagInfo: _.extend({}, flag.flagInfo, { isColumnChooserMigrated: true }) }
            );
          } else {
            pluginConfig.LegacyGridFlag.create(_.defaults({
              flagInfo: {
                isColumnChooserMigrated: true,
              },
            }, legacyGridFlagQuery));
          }

          return Promise.all([
            pluginConfig.LegacyColumns.findAll({ where: legacyQuery }),
            pluginConfig.LegacySelectedColumns.findAll({ where: legacyQuery }),
          ]).then(legacyDataProcesser);
        });
    }

    return Promise.resolve();
  }

  function removeRecord(name) {
    const promises = [];
    const record = getRecord(name);

    if (record) {
      promises.push(pluginConfig.Columns.destroy(record.id));
      if (name === toolbarEditor.get(saveListId).value) {
        promises.push(applyRecord('Custom'));
      }
    } else {
      throw new Error(`remove record error, the record with name: ${name} does not exist.`);
    }

    return Promise.all(promises).tap(() => {
      columnChooserAgent.triggerRecordRemovedEvent();
    });
  }

  function applyColumns({
    selectedKeys,
    savedName,
  }) {
    const name = savedName || 'Custom';

    setSelectedColumns(selectedKeys);

    return Promise.all([
      setRecord(name, selectedKeys),
      setSelectedRecord(name),
    ]).then(refreshMenu);
  }

  function attemptRemoveRecord(name) {
    const dialog = new Dialog({
      backdrop: true,
      keyboard: true,
      showHeader: false,
    }).setContent(new TextView({
      texts: [
        pluginConfig.i18n.getString('ColumnChooser_PromptBeforeRemove').replace('{0}', name),
      ],
      customClass: 'delete-column',
    }))
      .setState({
        buttons: [{
          text: pluginConfig.i18n.getString('ColumnChooser_ConfirmRemove'),
          name: 'save',
          class: 'confirm-remove btn-primary',
        }, {
          text: pluginConfig.i18n.getString('ColumnChooser_ConfirmCancel'),
          name: 'cancel',
          class: 'cancel-remove',
        }],
      })
      .render();

    dialog.$el.appendTo(document.body);
    dialog.show();

    dialog.on('clickedButton', (button) => {
      dialog.remove();
      if (button === 'save') {
        removeRecord(name).then(refreshMenu);
      }
    });
  }

  function destroyColumnChooser() {
    if (columnChooser) {
      columnChooser.remove();
      columnChooser = null;
    }
    if (customColumnPanel) {
      customColumnPanel.remove();
      customColumnPanel = null;
    }
  }

  function createColumnChooser(options) {
    destroyColumnChooser();

    const selected = getSelectedColumns();

    columnChooser = new ColumnChooser({
      i18n: pluginConfig.i18n,
      instrumentation: options.instrumentation,
      withColumnsChangeAnnouncement: true,
    }).setState({
      categories,
      selected,
      customColumnConfig,
    });

    return columnChooser;
  }

  function p$canSaveColumns({ savedName }) {
    return new Promise((resolve) => {
      if (_.find(pluginConfig.Columns.filter({ where: query }), _.matcher({ name: savedName }))) {
        const dialog = new Dialog({
          backdrop: true,
          keyboard: true,
          showHeader: false,
        }).setContent(new TextView({
          texts: [
            // eslint-disable-next-line no-restricted-globals
            pluginConfig.i18n.getString('ColumnChooser_ColumnNameAlreadyExistsPrompt').replace('{0}', savedName).replace('{{name}}', savedName),
            pluginConfig.i18n.getString('ColumnChooser_IfReplaceColumnName'),
          ],
        }))
          .setState({
            buttons: [{
              text: pluginConfig.i18n.getString('ColumnChooser_ConfirmReplace'),
              name: 'replace',
              class: 'confirm-replace btn-primary',
            }, {
              text: pluginConfig.i18n.getString('ColumnChooser_CancelReplace'),
              name: 'cancel',
              class: 'cancel-replace',
            }],
          })
          .render();

        dialog.$el.appendTo(document.body);
        dialog.show();

        dialog.on('clickedButton', (button) => {
          resolve(button === 'replace');
          dialog.remove();
        });

        dialog.on('willClose', () => resolve(false));
      } else {
        resolve(true);
      }
    });
  }

  function restoreToolbar() {
    toolbarEditor.restoreToolbar();
    toolbar.gridToolbarGroup.showAllToolbars();
    destroyColumnChooser();

    if (pluginConfig.isForVNext) {
      toolbarEditor.overrideWith(null);
    }
  }

  function showColumnChooser(options) {
    createColumnChooser(options);

    toolbarEditor.overrideWith(columnChooser);
    toolbar.gridToolbarGroup.hideAllToolbars();

    columnChooser.on('hide', (result) => {
      if (result) {
        p$canSaveColumns(result)
          .then((canSave) => {
            if (canSave) {
              return applyColumns(result)
                .then(() => restoreToolbar())
                .then(() => gridView.refresh(config.isDataFetchManagerSource))
                .catch(_.noop);
            }

            return Promise.resolve();
          });
      } else {
        restoreToolbar();
      }
    });
  }

  toolbarEditor.update({
    iconLeftGlyphName: pluginConfig.customToolbarIcon || '',
    id: dropdownId,
    button: {
      text: pluginConfig.i18n.getString('ColumnChooser_Entry'),
      iconLeft: ['glyphicon', 'toolbar-icon', 'toolbar-icon-left', 'icon-columnchooser-normal'],
      iconRight: ['glyphicon', 'toolbar-icon', 'glyphicon-triangle-bottom'],
    },
    menu: {
      items: [{
        text: pluginConfig.i18n.getString('ColumnChooser_ModifyColumns'),
        onClick: (e, options = {}) => _.defer(() => { showColumnChooser(options); }),
      }, {
        type: 'dropdown-divider',
      }, {
        type: 'dropdown-radio-group',
        id: saveListId,
        title: pluginConfig.i18n.getString('ColumnChooser_ApplySavedColumns'),
        items: [],
        removeText: pluginConfig.i18n.getString('ColumnChooser_RemoveSavedColumns'),
        onSelect: applyRecord,
        onRemove: attemptRemoveRecord,
        refreshMenu,
      }],
    },
  });

  const promises = [
    pluginConfig.Columns.findAll({ where: query }),
    pluginConfig.SelectedColumns.findAll({ where: query }),
  ];

  if (isCustomColumnEnabled) {
    promises.push(pluginConfig.CustomColumns.findAll({
      where: {
        project: 'CampaignUI',
        grid: getCustomColumnPreferenceName(config.name),
      },
    }));
  }

  Promise.all(promises)
    .then(setupCustomColumns)
    .then(legacyHandler)
    .then(getSelectedRecord)
    .then(applyRecord)
    .then(refreshMenu);

  if (enableRSAAdCustomizer && permissions.IsRSAAdCustomizerEnabled) {
    setupAdCustomizerAttributesColumns()
      .then(getSelectedRecord)
      .then(applyRecord);
  }

  return {
    remove() {
      destroyColumnChooser();
    },
    setSelectedColumns,
    columnChooserAgent,
    refreshMenu,
    restoreToolbar,
  };
});
